import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardComponent } from '../card/card.component';

/**
 * Reusable titled `<details>` card. Replaces the duplicated profile-section /
 * drivers-license-section shell — both wrap an `<app-card>` around a native
 * `<details>` with a header summary and arbitrary body content. Consumers
 * pass content via default `<ng-content>` and observe open-state via the
 * `(toggle)` output.
 *
 * The open state is sticky: it's seeded from `defaultOpen` once at init, and
 * thereafter only the user's clicks on the summary change it. The component
 * never re-evaluates `defaultOpen` mid-life so adding an N+1th item past a
 * threshold can't silently collapse the section.
 */
@Component({
  selector: 'app-collapsible-section',
  standalone: true,
  imports: [CommonModule, CardComponent],
  templateUrl: './collapsible-section.component.html',
  styleUrls: ['./collapsible-section.component.scss'],
})
export class CollapsibleSectionComponent implements OnInit {
  @Input() title = '';
  @Input() count: number | null = null;
  @Input() defaultOpen = true;
  /** When true, the host's open state stays open regardless of user clicks. */
  @Input() forceOpen = false;

  @Output() toggleChange = new EventEmitter<boolean>();

  userOpenState = true;

  get isOpen(): boolean {
    return this.forceOpen || this.userOpenState;
  }

  ngOnInit(): void {
    this.userOpenState = this.defaultOpen;
  }

  onToggle(event: Event): void {
    const open = (event.target as HTMLDetailsElement).open;
    this.userOpenState = open;
    this.toggleChange.emit(open);
  }
}
